import React, { useEffect, useState } from "react";
import { Button, message, Steps } from "antd";
import { useNavigate } from "react-router-dom";
import { SolutionOutlined } from "@ant-design/icons";
import axiosInstance from "../../Config/axiosConfig";
import "./SignUp.css";
import Logo from "../../Components/Logo/Logo";

// Import step components
import PersonalInfoStep from "../../Components/PersonalInfoStep/PersonalInfoStep";
import AccountTypeStep from "../../Components/AccountTypeStep/AccountTypeStep";
import PasswordStep from "../../Components/PasswordStep/PasswordStep";
import ReviewStep from "../../Components/ReviewStep/ReviewStep";

const { Step } = Steps;

function Signup({ setLoggedIn, deviceId, userIp }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Controlled state for all inputs
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
  });
  const [userType, setUserType] = useState(null);
  const [children, setChildren] = useState([{ name: "", class_id: null }]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isPersonalInfoValid, setIsPersonalInfoValid] = useState(false);

  // Helper validations
  const isEmail = (value) => /\S+@\S+\.\S+/.test(value);

  const isPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    return /^(0|255)?[0-9]{9,12}$/.test(digits);
  };

  const formatPhoneNumberForBackend = (input) => {
    let digits = input.replace(/\D/g, "");
    if (digits.startsWith("0")) digits = digits.substring(1);
    if (!digits.startsWith("255")) digits = "255" + digits;
    return digits.substring(0, 12);
  };

  const handlePhoneChange = (e) => setPhoneNumber(e.target.value);
  const handleEmailChange = (e) => setEmail(e.target.value);
  const handleFirstNameChange = (e) => setFirstName(e.target.value);
  const handleLastNameChange = (e) => setLastName(e.target.value);

const handlePasswordChange = (e) => {
  const val = e.target.value;
  setPassword(val);
  setPasswordErrors({
    isValidLength: val.length >= 4,
  });
};

  const handleConfirmPasswordChange = (e) => setConfirmPassword(e.target.value);

  const nextStep = () => {
  if (currentStep === 0) {
    if (!firstName || !lastName || (!email && !phoneNumber)) {
      return message.error("Please fill all personal info fields or provide email/phone.");
    }
  }

  if (currentStep === 1) {
    if (!selectedClass) return message.error("Select your class");
  }

  if (currentStep === 2) {
  if (!password || password !== confirmPassword) {
    return message.error("Passwords must match");
  }

  if (password.length < 4) {
    return message.error("Password must be at least 4 characters long");
  }
}

  setCurrentStep(currentStep + 1);
};

  const prevStep = () => setCurrentStep(currentStep - 1);

const handleSignup = async () => {
  if (!acceptedTerms) return message.error("Accept the terms to continue");

  if (!email && !phoneNumber)
    return message.error("Provide at least email or phone number")

  setLoading(true);

  try {
   const requestData = {
      name: `${firstName} ${lastName}`,
      password,
      role: 2,
      isStudent: true, // default
      termsAccepted: true,
      students: [{
        name: `${firstName} ${lastName}`,
        class_id: selectedClass,
      }],
    };

    if (email && isEmail(email)) requestData.email = email;
    if (phoneNumber && isPhoneNumber(phoneNumber))
      requestData.phoneNumber = formatPhoneNumberForBackend(phoneNumber);

    // Handle students data - FIXED LOGIC
    if (userType === "student") {
      requestData.students = [{
        name: `${firstName} ${lastName}`,
        class_id: selectedClass,
      }];
      
    } else if (userType === "parent") {
      const validChildren = children.filter(child => 
        child.name && child.name.trim() && child.class_id
      );
      
      if (validChildren.length === 0) {
        message.error("Please add at least one valid child");
        setLoading(false);
        return;
      }
      
      requestData.students = children;
    }

    console.log("=== REQUEST DATA ===", requestData);

    // Convert to query params for Swagger
    const params = new FormData();

    Object.keys(requestData).forEach((key) => {
      if (key === "students" && Array.isArray(requestData.students)) {
              requestData.students.forEach((student, idx) => {
          params.append(`students[${idx}][name]`, student.name);
          params.append(`students[${idx}][class_id]`, student.class_id);
        });
      } else {
        // Handle all other fields
        params.append(key, requestData[key]);
      }
    });

    console.log("=== FINAL PARAMS ===", params.toString());

    const response = await axiosInstance.post(
      `/user/signup`,
      params,
      {
        headers: { 
          UserDevice: deviceId, 
          UserIPAddress: userIp,
          "Content-Type": "multipart/form-data"
        },
      }
    );
    
    console.log("=== SIGNUP RESPONSE ===", response);
    
    if (response.data.code === 200) {
      message.success(response.data.message || "Registration successful!");
      navigate("/signin", {
        state: {
          email: requestData.email,
          phoneNumber: requestData.phoneNumber,
          deviceId,
          userIp,
        },
      });
    } else {
      message.error(response.data.message);
    }
  } catch (err) {
    console.error("=== SIGNUP ERROR ===", err);
    
    // More detailed error handling
    if (err.response) {
      console.error("Error response:", err.response.data);
      if (err.response.data && err.response.data.errors) {
        message.error(JSON.stringify(err.response.data.errors));
      } else {
        message.error(err.response.data?.message || "Signup failed. Try again.");
      }
    } else {
      message.error("Signup failed. Try again.");
    }
  } finally {
    setLoading(false);
  }
};

const renderStepContent = () => {
  switch (currentStep) {
    case 0:
      return (
        <PersonalInfoStep
        firstName={firstName}
        lastName={lastName}
        email={email}
        phoneNumber={phoneNumber}
        handleFirstNameChange={handleFirstNameChange}
        handleLastNameChange={handleLastNameChange}
        handleEmailChange={handleEmailChange}
        handlePhoneChange={handlePhoneChange}
        onValidationChange={setIsPersonalInfoValid}
      />
      );
    case 1:
      return (
        <AccountTypeStep
          selectedClass={selectedClass}
          setSelectedClass={setSelectedClass}
        />
      );
    case 2:
      return (
        <PasswordStep
          password={password}
          confirmPassword={confirmPassword}
          handlePasswordChange={handlePasswordChange}
          handleConfirmPasswordChange={handleConfirmPasswordChange}
          passwordErrors={passwordErrors}
        />
      );
    case 3:
      return (
        <ReviewStep
          firstName={firstName}
          lastName={lastName}
          email={email}
          phoneNumber={phoneNumber}
          password={password}
          selectedClass={selectedClass}
          acceptedTerms={acceptedTerms}
          setAcceptedTerms={setAcceptedTerms}
        />
      );
    default:
      return null;
  }
};

    // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // treat <= 768px as mobile
    };
    handleResize(); // run on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="signup-page-container">
      <div className="signup-container">
        <Logo />
        <h4>Sign up for your account</h4>
        <span className="signup-text">Welcome! Register your credentials</span>

      <Steps current={currentStep} className="signup-steps"
            direction={isMobile ? "horizontal" : "horizontal"} // 👈 responsive direction
            size={isMobile ? "small" : "default"}            // 👈 smaller on mobile
            responsive={false}
  
        >
        <Step title="--------" />
        <Step title="--------" />
        <Step title="--------" />
        <Step title="" />
      </Steps>

        {renderStepContent()}

        <div className="step-actions">
          {currentStep > 0 && <Button onClick={prevStep}>Previous</Button>}
          {currentStep < 3 && <Button type="primary" onClick={nextStep}  disabled={!isPersonalInfoValid}>Next</Button>}
          {currentStep === 3 && (
            <Button type="primary" onClick={handleSignup} loading={loading}>
              {loading ? "Processing..." : "Sign Up"}
            </Button>
          )}
        </div>

        <p className="signin-link">
          Already have an account? <a href="/signin">Sign In</a>
        </p>
      </div>
    </div>
  );
}

export default Signup;
